import { ReactElement, ReactNode, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
    TasksAndAlertsAnalyticsTimeseriesItem,
    TasksAndAlertsAnalyticsWeek
} from '@/api';
import {
    ActivityOverviewData,
    AlertTypeData,
    DayOfTheWeekActivityData,
    RealVsFalseAlertData,
    SingleValueData,
    TasksAndAlertsAnalyticsContext,
    TasksAndAlertsAnalyticsContextData,
    TasksAndAlertsChartData
} from './context';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '@/tasks/providers';
import { blue, concrete, mint, orange, red, yellow } from '@/ui/styles';
import { useFormatDate, useTimeSettings } from '@/common/hooks';
import {
    getEndOfDayInTimezone,
    getStartOfDayInTimezone
} from '@/common/util/format-date';
import { useAnalyticsTranslation } from '../../hooks';
import { useFetchTasksAndAlerts } from '../../queries';
import { buildAnalyticsQueryFilter } from '../../util/analytics-query-filter';
import { useReportsFilter } from '../reports-filter-provider';

type TasksAndAlertsAnalyticsProviderProps = {
    readonly children?: ReactNode;
};

export function TasksAndAlertsAnalyticsProvider({
    children
}: TasksAndAlertsAnalyticsProviderProps): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { company } = useCompanyContext();
    const { teamIds, userIds, deviceIds } = useTaskFilter();
    const { range, period } = useReportsFilter();
    const { formatDateRange } = useFormatDate();
    const { timezone } = useTimeSettings();

    const query = useMemo(() => {
        return {
            ...buildAnalyticsQueryFilter({
                companyId: company.id,
                teamIds,
                userIds,
                deviceIds,
                timezone,
                from: getStartOfDayInTimezone(range.from, timezone),
                to: getEndOfDayInTimezone(range.to, timezone)
            }),
            unit: period
        };
    }, [company.id, timezone, teamIds, userIds, deviceIds, range, period]);

    const { data, status } = useFetchTasksAndAlerts(query);

    const {
        dateString,
        activityOverviewData,
        tasksAndAlertsData,
        dayOfTheWeekActivityData,
        realVsFalseAlertData,
        alertTypeData,
        totalTasksData,
        totalAlertsData,
        totalRealAlertsData
    } = useMemo<{
        dateString: string | null;
        activityOverviewData: ActivityOverviewData[];
        tasksAndAlertsData: TasksAndAlertsChartData[];
        dayOfTheWeekActivityData: DayOfTheWeekActivityData[];
        realVsFalseAlertData: RealVsFalseAlertData[];
        alertTypeData: AlertTypeData[];
        totalTasksData: SingleValueData | null;
        totalAlertsData: SingleValueData | null;
        totalRealAlertsData: SingleValueData | null;
    }>(() => {
        if (!data) {
            return {
                dateString: null,
                activityOverviewData: [],
                tasksAndAlertsData: [],
                dayOfTheWeekActivityData: [],
                realVsFalseAlertData: [],
                alertTypeData: [],
                totalTasksData: null,
                totalAlertsData: null,
                totalRealAlertsData: null
            };
        }

        type TransformedTasksAndAnalyticsItem = {
            start: Date;
            end?: Date;
            taskCount: number;
            alertCount: number;
            falseAlarmCount: number;
            realAlarmCount: number;
            expiredAlertCount: number;
            manualAlertCount: number;
            fallAlertCount: number;
            geofenceAlertCount: number;
            tiltAlertCount: number;
        };

        function parseYYYYMMDD(dateString: string): Date {
            const year = parseInt(dateString.slice(0, 4), 10);
            const month = parseInt(dateString.slice(4, 6), 10);
            const day = parseInt(dateString.slice(6, 8), 10);
            return DateTime.fromObject(
                { year, month, day },
                { zone: timezone }
            ).toJSDate();
        }

        function transformData(
            item: TasksAndAlertsAnalyticsTimeseriesItem
        ): TransformedTasksAndAnalyticsItem {
            const start = parseYYYYMMDD(item.st);
            const end = item.en ? parseYYYYMMDD(item.en) : undefined;
            return {
                start,
                end,
                taskCount: item.t ?? 0,
                alertCount: item.a ?? 0,
                falseAlarmCount: item.fa ?? 0,
                realAlarmCount: item.ra ?? 0,
                expiredAlertCount: item.ate ?? 0,
                manualAlertCount: item.atm ?? 0,
                fallAlertCount: item.atf ?? 0,
                geofenceAlertCount: item.atg ?? 0,
                tiltAlertCount: item.att ?? 0
            };
        }

        const dateString = formatDateRange(
            new Date(data.from),
            new Date(data.to)
        );

        /**
         * Activity Overview Data
         */
        const _activityOverviewData: Array<ActivityOverviewData> = [];
        function appendActivityOverviewData(
            item: TransformedTasksAndAnalyticsItem
        ) {
            _activityOverviewData.push({
                start: item.start,
                end: item.end,
                value: 1,
                taskCount: item.taskCount,
                alertCount: item.alertCount,
                fill:
                    item.alertCount > 0
                        ? red
                        : item.taskCount > 0
                          ? blue
                          : concrete
            });
        }

        /**
         * Tasks and Alerts Chart Data
         */
        const _tasksAndAlertsData: Array<TasksAndAlertsChartData> = [];
        function appendTasksAndAlertsData(
            item: TransformedTasksAndAnalyticsItem
        ) {
            _tasksAndAlertsData.push({
                taskCount: item.taskCount,
                alertCount: item.alertCount,
                start: item.start,
                end: item.end
            });
        }

        /**
         * Day of the Week Activity Data
         */
        function finaliseDayOfTheWeekActivityData(
            week: TasksAndAlertsAnalyticsWeek
        ): DayOfTheWeekActivityData[] {
            return [
                {
                    taskCount: week.mon.t,
                    alertCount: week.mon.a,
                    dayOfTheWeek: t('shared.days.monday.name')
                },
                {
                    taskCount: week.tue.t,
                    alertCount: week.tue.a,
                    dayOfTheWeek: t('shared.days.tuesday.name')
                },
                {
                    taskCount: week.wed.t,
                    alertCount: week.wed.a,
                    dayOfTheWeek: t('shared.days.wednesday.name')
                },
                {
                    taskCount: week.thu.t,
                    alertCount: week.thu.a,
                    dayOfTheWeek: t('shared.days.thursday.name')
                },
                {
                    taskCount: week.fri.t,
                    alertCount: week.fri.a,
                    dayOfTheWeek: t('shared.days.friday.name')
                },
                {
                    taskCount: week.sat.t,
                    alertCount: week.sat.a,
                    dayOfTheWeek: t('shared.days.saturday.name')
                },
                {
                    taskCount: week.sun.t,
                    alertCount: week.sun.a,
                    dayOfTheWeek: t('shared.days.sunday.name')
                }
            ];
        }

        /**
         * Real vs False Alert Data
         */
        const _realAlarmData: RealVsFalseAlertData = {
            value: 0,
            fill: red,
            label: t('shared.alerts.real')
        };
        const _falseAlarmData: RealVsFalseAlertData = {
            value: 0,
            fill: blue,
            label: t('shared.alerts.false')
        };
        function appendRealVsFalseAlert(
            item: TransformedTasksAndAnalyticsItem
        ) {
            if (item.realAlarmCount > 0) {
                _realAlarmData.value += item.realAlarmCount;
            }
            if (item.falseAlarmCount > 0) {
                _falseAlarmData.value += item.falseAlarmCount;
            }
        }
        function finaliseRealVsFalseAlertData(): RealVsFalseAlertData[] {
            const _data: RealVsFalseAlertData[] = [];
            if (_realAlarmData.value > 0) {
                _data.push(_realAlarmData);
            }
            if (_falseAlarmData.value > 0) {
                _data.push(_falseAlarmData);
            }
            return _data;
        }

        /**
         * Alert Type Data
         */
        const _expiredAlertData: AlertTypeData = {
            label: t('shared.alerts.expired'),
            value: 0,
            fill: red
        };
        const _manualAlertData: AlertTypeData = {
            label: t('shared.alerts.manual'),
            value: 0,
            fill: blue
        };
        const _fallAlertData: AlertTypeData = {
            label: t('shared.alerts.fall'),
            value: 0,
            fill: orange
        };
        const _geofenceAlertData: AlertTypeData = {
            label: t('shared.alerts.geofence'),
            value: 0,
            fill: mint
        };
        const _tiltAlertData: AlertTypeData = {
            label: t('shared.alerts.tilt'),
            value: 0,
            fill: yellow
        };
        function appendAlertTypeData(item: TransformedTasksAndAnalyticsItem) {
            if (item.expiredAlertCount > 0) {
                _expiredAlertData.value += item.expiredAlertCount;
            }
            if (item.manualAlertCount > 0) {
                _manualAlertData.value += item.manualAlertCount;
            }
            if (item.fallAlertCount > 0) {
                _fallAlertData.value += item.fallAlertCount;
            }
            if (item.geofenceAlertCount > 0) {
                _geofenceAlertData.value += item.geofenceAlertCount;
            }
            if (item.tiltAlertCount > 0) {
                _tiltAlertData.value += item.tiltAlertCount;
            }
        }
        function finaliseAlertTypeData(): AlertTypeData[] {
            const _data: AlertTypeData[] = [];
            if (_expiredAlertData.value > 0) {
                _data.push(_expiredAlertData);
            }
            if (_manualAlertData.value > 0) {
                _data.push(_manualAlertData);
            }
            if (_fallAlertData.value > 0) {
                _data.push(_fallAlertData);
            }
            if (_geofenceAlertData.value > 0) {
                _data.push(_geofenceAlertData);
            }
            if (_tiltAlertData.value > 0) {
                _data.push(_tiltAlertData);
            }
            return _data;
        }

        /**
         * Total Tasks and Alerts Data
         */
        let totalTasksCount: number = 0;
        let totalAlertsCount: number = 0;
        let totalRealAlertsCount: number = 0;
        function sumTotalTasksAndAlertsData(
            item: TransformedTasksAndAnalyticsItem
        ) {
            totalTasksCount += item.taskCount;
            totalAlertsCount += item.alertCount;
            totalRealAlertsCount += item.realAlarmCount;
        }

        data.timeseries.forEach((item) => {
            const transformed = transformData(item);
            appendActivityOverviewData(transformed);
            appendTasksAndAlertsData(transformed);
            appendRealVsFalseAlert(transformed);
            appendAlertTypeData(transformed);
            sumTotalTasksAndAlertsData(transformed);
        });

        return {
            dateString,
            activityOverviewData: _activityOverviewData,
            tasksAndAlertsData: _tasksAndAlertsData,
            dayOfTheWeekActivityData: finaliseDayOfTheWeekActivityData(
                data.week
            ),
            realVsFalseAlertData: finaliseRealVsFalseAlertData(),
            alertTypeData: finaliseAlertTypeData(),
            totalTasksData: { value: totalTasksCount, dateString },
            totalAlertsData: { value: totalAlertsCount, dateString },
            totalRealAlertsData: { value: totalRealAlertsCount, dateString }
        };
    }, [data, formatDateRange, timezone, t]);

    const value: TasksAndAlertsAnalyticsContextData = {
        status,
        activityOverviewData,
        tasksAndAlertsData,
        dayOfTheWeekActivityData,
        realVsFalseAlertData,
        alertTypeData,
        totalTasksData,
        totalAlertsData,
        totalRealAlertsData,
        dateString
    };

    return (
        <TasksAndAlertsAnalyticsContext.Provider value={value}>
            {children}
        </TasksAndAlertsAnalyticsContext.Provider>
    );
}
